// ── toErrorPresentation — wire -> presentation normalizer ────────────────────
// Pure, no React. STRUCTURAL on purpose: it reads fields off whatever error
// value arrives (ApiError from @platform/agent-sdk, a raw ProblemDetails wire
// object, or an unknown/plain Error) — never `instanceof`, which is brittle
// across separately bundled MFE copies. Spec: UI Specs: errorzen v2.1
// §toErrorPresentation (FR-027, Epic ASC-6291).

import { isKnownErrorCode, isProblemDetails, isRetryable } from '@platform/errorzen-contracts'

import { GENERIC_ERROR_COPY, resolveErrorCopy } from './copy'

export interface ErrorPresentation {
  title: string
  description: string
  /** The error's `request_id` (or ApiError `requestId`) — null when absent. */
  supportId: string | null
  retryable: boolean
  code: string | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function readSupportId(record: Record<string, unknown> | null): string | null {
  if (record === null) return null
  if (typeof record.request_id === 'string' && record.request_id !== '') return record.request_id
  if (typeof record.requestId === 'string' && record.requestId !== '') return record.requestId
  return null
}

/**
 * Normalize any error value into `ErrorState`'s primitive props.
 *
 * Resolution rules (in order):
 * 1. KNOWN `code` — localized copy from the FE copy map (the wire/registry
 *    English `title` is the fallback only, via the total `en` map).
 * 2. Successfully VALIDATED problem (open wire schema) — wire `title` + safe
 *    `detail`. An ApiError carries the validated body on `.body`, so both
 *    layers are checked.
 * 3. Anything else (unvalidated legacy body, plain `Error`) — GENERIC copy.
 *    The unvalidated text is NOT shown verbatim: an unvalidated body may
 *    carry leaked internals. Safety rule, not a copy preference.
 */
export function toErrorPresentation(error: unknown, locale?: string): ErrorPresentation {
  const record = asRecord(error)
  const code = typeof record?.code === 'string' ? record.code : null
  const supportId = readSupportId(record)
  const retryable = isRetryable(record as { retryable?: boolean | null } | null)

  // Rule 1 — a known code drives copy, resolved centrally (never per call site).
  if (code !== null && isKnownErrorCode(code)) {
    const copy = resolveErrorCopy(code, locale)
    return { title: copy.title, description: copy.description, supportId, retryable, code }
  }

  // Rule 2 — validated problem with a code this bundle has never seen.
  const problem = isProblemDetails(error)
    ? error
    : record !== null && isProblemDetails(record.body)
      ? record.body
      : null
  if (problem !== null) {
    return {
      title: problem.title,
      description: problem.detail ?? GENERIC_ERROR_COPY.description,
      supportId: supportId ?? problem.request_id,
      retryable: retryable || isRetryable(problem),
      code: code ?? problem.code,
    }
  }

  // Rule 3 — the safe default.
  return {
    title: GENERIC_ERROR_COPY.title,
    description: GENERIC_ERROR_COPY.description,
    supportId,
    retryable,
    code,
  }
}
